import React, { useState, useEffect } from "react";
import styled from "styled-components";
import PageHeader from "../components/PageHeader";
import Card from "../components/Card";
import DataTable from "../components/DataTable";
import {
  fetchMedications,
  fetchStates,
  fetchPharmacies,
  createMedicine,
  updateMedicine,
  deleteMedicine,
  addDosageInstruction,
  api
} from "../services/api";

// Styled Components
const SearchBar = styled.div`
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;

  input {
    width: 100%;
    max-width: 400px;
    padding: 0.75rem;
    border-radius: var(--border-radius);
    border: 1px solid var(--border-color);
    font-size: 1rem;
    background-color: var(--bg-primary);
    color: var(--text-primary);

    &:focus {
      outline: none;
      border-color: var(--primary-color);
      box-shadow: 0 0 0 2px rgba(99, 91, 255, 0.2);
    }
  }
`;

const FilterSortContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FilterGroup = styled.div`
  flex: 1;
  min-width: 200px;
`;

const FilterLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  font-size: 0.875rem;
  color: var(--text-primary);
`;

const FilterSelect = styled.select`
  width: 100%;
  padding: 0.6rem;
  border-radius: var(--border-radius);
  border: 1px solid var(--border-color);
  background-color: var(--bg-primary);
  color: var(--text-primary);
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(99, 91, 255, 0.2);
  }
`;

const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.25rem;
  background-color: var(--primary-color);
  color: white;
  font-weight: 600;
  border-radius: var(--border-radius);
  border: none;
  cursor: pointer;
  text-decoration: none;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--primary-dark);
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }

  svg {
    margin-right: 0.5rem;
  }
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 20px;
  background-color: var(--gray-200);
  color: var(--gray-800);
  font-size: 0.75rem;
  font-weight: 500;
  margin-right: 0.25rem;
  margin-bottom: 0.25rem;
  min-width: 20px;
  text-align: center;

  &:hover {
    background-color: var(--gray-100);
  }
`;

const PharmacyBadge = styled(Badge)`
  background-color: rgba(var(--info-light-rgb), 0.3);
  color: var(--info-color);
  border: 1px solid rgba(var(--info-color-rgb), 0.3);
  font-size: 0.8rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  font-weight: 600;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;

  &:hover {
    background-color: var(--info-light-hover);
  }
`;

const StateBadge = styled(Badge)`
  background-color: ${(props) =>
    props.style?.backgroundColor || "var(--gray-200)"};
  color: ${(props) => props.style?.color || "var(--text-primary)"};
  border: 1px solid
    ${(props) => props.style?.borderColor || "var(--border-color)"};
  font-size: 0.7rem;
  min-width: 25px;
  font-weight: 600;
  padding: 0.2rem 0.4rem;
  border-radius: 10px;
`;

const VariantBadge = styled(Badge)`
  background-color: rgba(var(--primary-light-rgb), 0.2);
  color: var(--variant-text-color, var(--text-primary));
  border: 1px solid rgba(var(--primary-color-rgb), 0.2);
  font-size: 0.7rem;
  font-weight: 600;
`;

const PriceTag = styled.span`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 1.1rem;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: var(--primary-color);
  margin-right: 10px;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const DeleteActionButton = styled(ActionButton)`
  color: var(--error-color);
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: var(--card-bg);
  padding: 2rem;
  border-radius: var(--border-radius);
  width: 800px;
  max-width: 95%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  color: var(--text-primary);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: var(--text-primary);
  font-size: 1.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-primary);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormRow = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const FormColumn = styled.div`
  flex: 1;
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: var(--text-primary);
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: 1rem;
  background-color: var(--bg-primary);
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(99, 91, 255, 0.2);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: 1rem;
  background-color: var(--bg-primary);
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(99, 91, 255, 0.2);
  }

  option {
    background-color: var(--bg-primary);
    color: var(--text-primary);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: 1rem;
  background-color: var(--bg-primary);
  color: var(--text-primary);
  min-height: 100px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(99, 91, 255, 0.2);
  }
`;

const StateTag = styled.div`
  display: inline-block;
  padding: 0.4rem 0.6rem;
  border-radius: var(--border-radius);
  background-color: ${(props) =>
    props.selected ? "var(--primary-color)" : "var(--bg-primary)"};
  color: ${(props) => (props.selected ? "white" : "var(--text-primary)")};
  border: 1px solid
    ${(props) =>
      props.selected ? "var(--primary-color)" : "var(--border-color)"};
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  margin: 0.15rem;
  min-width: 25px;
  text-align: center;

  &:hover {
    background-color: ${(props) =>
      props.selected ? "var(--primary-dark)" : "var(--gray-100)"};
  }
`;

const PharmacyStateTag = styled(StateTag)`
  background-color: ${(props) =>
    props.selected ? "var(--primary-color)" : "var(--info-light)"};
  color: ${(props) => (props.selected ? "white" : "var(--info-color)")};
  border: 2px solid
    ${(props) =>
      props.selected ? "var(--primary-color)" : "var(--info-color)"};
  font-weight: 500;

  &:hover {
    background-color: ${(props) =>
      props.selected ? "var(--primary-dark)" : "var(--info-light-hover)"};
  }
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const MedicineInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  .medicine-name {
    font-weight: 600;
    font-size: 1.125rem;
    color: var(--text-primary);
  }

  .medicine-sku {
    font-size: 0.75rem;
    color: var(--text-secondary, #6c757d);
    opacity: 0.8;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

const Spinner = styled.div`
  border: 4px solid rgba(0, 0, 0, 0.1);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border-left-color: var(--primary-color);
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const ErrorContainer = styled.div`
  color: var(--error-color);
  text-align: center;
  padding: 2rem;
  background-color: var(--error-light);
  border-radius: var(--border-radius);
  margin: 1rem 0;
`;

const ConfirmationModal = styled(Modal)``;

const ConfirmationContent = styled.div`
  background-color: var(--card-bg);
  padding: 2rem;
  border-radius: var(--border-radius);
  width: 400px;
  max-width: 95%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  color: var(--text-primary);
`;

const ConfirmationActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
`;

const CancelButton = styled(AddButton)`
  background-color: var(--gray-400);

  &:hover {
    background-color: var(--gray-500);
  }
`;

const DeleteButton = styled(AddButton)`
  background-color: var(--error-color);

  &:hover {
    background-color: var(--error-dark);
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const FilterChip = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 0.75rem;
  background-color: var(--bg-secondary);
  border-radius: 20px;
  font-size: 0.75rem;
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
  border: 1px solid var(--border-color);

  .close-btn {
    margin-left: 0.5rem;
    cursor: pointer;
    color: var(--text-secondary);

    &:hover {
      color: var(--error-color);
    }
  }
`;

const ActiveFiltersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;

const ClearFiltersButton = styled.button`
  background: none;
  border: none;
  color: var(--primary-color);
  cursor: pointer;
  font-size: 0.75rem;
  padding: 0.4rem 0.75rem;
  margin-bottom: 0.5rem;

  &:hover {
    text-decoration: underline;
  }
`;

// Notice component for view-only access
const AccessNotice = styled.div`
  background-color: rgba(255, 152, 0, 0.1);
  border: 1px solid rgba(255, 152, 0, 0.3);
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #f57c00;
  font-size: 0.9rem;

  svg {
    width: 20px;
    height: 20px;
    min-width: 20px;
  }
`;

function MedicinesPage() {
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredMedicines, setFilteredMedicines] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    medicine_name: "",
    medicine_sku: "",
    description: "",
    pharmacies: [],
    states: [],
    dosage_instructions: [],
    variants: []
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [states, setStates] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [medicineToDelete, setMedicineToDelete] = useState(null);
  const [dosageInstruction, setDosageInstruction] = useState("");
  const [variantForm, setVariantForm] = useState({
    quantity: "",
    unit: "tablet",
    note: ""
  });

  // New state for filtering and sorting
  const [filters, setFilters] = useState({
    pharmacy: "",
    state: "",
    hasVariants: ""
  });
  const [sortConfig, setSortConfig] = useState({
    key: "medicine_name",
    direction: "asc"
  });
  const [activeFilters, setActiveFilters] = useState({});

  // User permission state
  const [currentUser, setCurrentUser] = useState(null);
  const [hasEditPermission, setHasEditPermission] = useState(false);

  // Get state colors for badges
  const getStateColor = (abbreviation) => {
    if (!abbreviation)
      return { color: "var(--gray-400)", textColor: "var(--text-primary)" };

    // Create a deterministic color based on the state abbreviation
    const colors = {
      AL: { color: "#E3F2FD", textColor: "#1565C0", borderColor: "#90CAF9" }, // Alabama - Light Blue
      AK: { color: "#E8F5E9", textColor: "#2E7D32", borderColor: "#A5D6A7" }, // Alaska - Light Green
      AZ: { color: "#FFF3E0", textColor: "#E65100", borderColor: "#FFCC80" }, // Arizona - Light Orange
      AR: { color: "#F3E5F5", textColor: "#6A1B9A", borderColor: "#CE93D8" }, // Arkansas - Light Purple
      CA: { color: "#E1F5FE", textColor: "#0277BD", borderColor: "#81D4FA" }, // California - Light Blue
      CO: { color: "#FFFDE7", textColor: "#F57F17", borderColor: "#FFF59D" }, // Colorado - Light Yellow
      CT: { color: "#E8EAF6", textColor: "#303F9F", borderColor: "#9FA8DA" }, // Connecticut - Light Indigo
      DE: { color: "#FCE4EC", textColor: "#C2185B", borderColor: "#F48FB1" }, // Delaware - Light Pink
      FL: { color: "#F1F8E9", textColor: "#558B2F", borderColor: "#C5E1A5" }, // Florida - Light Green
      GA: { color: "#FFEBEE", textColor: "#B71C1C", borderColor: "#EF9A9A" }, // Georgia - Light Red
      HI: { color: "#E0F7FA", textColor: "#00838F", borderColor: "#80DEEA" }, // Hawaii - Light Cyan
      ID: { color: "#FFF8E1", textColor: "#FF8F00", borderColor: "#FFE082" }, // Idaho - Light Amber
      IL: { color: "#EFEBE9", textColor: "#4E342E", borderColor: "#BCAAA4" }, // Illinois - Light Brown
      IN: { color: "#E0F2F1", textColor: "#00695C", borderColor: "#80CBC4" }, // Indiana - Light Teal
      IA: { color: "#F9FBE7", textColor: "#827717", borderColor: "#E6EE9C" }, // Iowa - Light Lime
      KS: { color: "#E1F5FE", textColor: "#01579B", borderColor: "#81D4FA" }, // Kansas - Light Blue
      KY: { color: "#F3E5F5", textColor: "#6A1B9A", borderColor: "#CE93D8" }, // Kentucky - Light Purple
      LA: { color: "#E8F5E9", textColor: "#2E7D32", borderColor: "#A5D6A7" }, // Louisiana - Light Green
      ME: { color: "#E3F2FD", textColor: "#1565C0", borderColor: "#90CAF9" }, // Maine - Light Blue
      MD: { color: "#FFF3E0", textColor: "#E65100", borderColor: "#FFCC80" }, // Maryland - Light Orange
      MA: { color: "#E8EAF6", textColor: "#303F9F", borderColor: "#9FA8DA" }, // Massachusetts - Light Indigo
      MI: { color: "#E0F7FA", textColor: "#00838F", borderColor: "#80DEEA" }, // Michigan - Light Cyan
      MN: { color: "#F1F8E9", textColor: "#558B2F", borderColor: "#C5E1A5" }, // Minnesota - Light Green
      MS: { color: "#FFEBEE", textColor: "#B71C1C", borderColor: "#EF9A9A" }, // Mississippi - Light Red
      MO: { color: "#FFF8E1", textColor: "#FF8F00", borderColor: "#FFE082" }, // Missouri - Light Amber
      MT: { color: "#EFEBE9", textColor: "#4E342E", borderColor: "#BCAAA4" }, // Montana - Light Brown
      NE: { color: "#E0F2F1", textColor: "#00695C", borderColor: "#80CBC4" }, // Nebraska - Light Teal
      NV: { color: "#F9FBE7", textColor: "#827717", borderColor: "#E6EE9C" }, // Nevada - Light Lime
      NH: { color: "#E1F5FE", textColor: "#01579B", borderColor: "#81D4FA" }, // New Hampshire - Light Blue
      NJ: { color: "#F3E5F5", textColor: "#6A1B9A", borderColor: "#CE93D8" }, // New Jersey - Light Purple
      NM: { color: "#E8F5E9", textColor: "#2E7D32", borderColor: "#A5D6A7" }, // New Mexico - Light Green
      NY: { color: "#E3F2FD", textColor: "#1565C0", borderColor: "#90CAF9" }, // New York - Light Blue
      NC: { color: "#FFF3E0", textColor: "#E65100", borderColor: "#FFCC80" }, // North Carolina - Light Orange
      ND: { color: "#E8EAF6", textColor: "#303F9F", borderColor: "#9FA8DA" }, // North Dakota - Light Indigo
      OH: { color: "#E0F7FA", textColor: "#00838F", borderColor: "#80DEEA" }, // Ohio - Light Cyan
      OK: { color: "#F1F8E9", textColor: "#558B2F", borderColor: "#C5E1A5" }, // Oklahoma - Light Green
      OR: { color: "#FFEBEE", textColor: "#B71C1C", borderColor: "#EF9A9A" }, // Oregon - Light Red
      PA: { color: "#FFF8E1", textColor: "#FF8F00", borderColor: "#FFE082" }, // Pennsylvania - Light Amber
      RI: { color: "#EFEBE9", textColor: "#4E342E", borderColor: "#BCAAA4" }, // Rhode Island - Light Brown
      SC: { color: "#E0F2F1", textColor: "#00695C", borderColor: "#80CBC4" }, // South Carolina - Light Teal
      SD: { color: "#F9FBE7", textColor: "#827717", borderColor: "#E6EE9C" }, // South Dakota - Light Lime
      TN: { color: "#E1F5FE", textColor: "#01579B", borderColor: "#81D4FA" }, // Tennessee - Light Blue
      TX: { color: "#F3E5F5", textColor: "#6A1B9A", borderColor: "#CE93D8" }, // Texas - Light Purple
      UT: { color: "#E8F5E9", textColor: "#2E7D32", borderColor: "#A5D6A7" }, // Utah - Light Green
      VT: { color: "#E3F2FD", textColor: "#1565C0", borderColor: "#90CAF9" }, // Vermont - Light Blue
      VA: { color: "#FFF3E0", textColor: "#E65100", borderColor: "#FFCC80" }, // Virginia - Light Orange
      WA: { color: "#E8EAF6", textColor: "#303F9F", borderColor: "#9FA8DA" }, // Washington - Light Indigo
      WV: { color: "#E0F7FA", textColor: "#00838F", borderColor: "#80DEEA" }, // West Virginia - Light Cyan
      WI: { color: "#F1F8E9", textColor: "#558B2F", borderColor: "#C5E1A5" }, // Wisconsin - Light Green
      WY: { color: "#FFEBEE", textColor: "#B71C1C", borderColor: "#EF9A9A" }, // Wyoming - Light Red
      DC: { color: "#FFF8E1", textColor: "#FF8F00", borderColor: "#FFE082" } // District of Columbia - Light Amber
    };

    return (
      colors[abbreviation] || {
        color: "var(--gray-400)",
        textColor: "var(--text-primary)"
      }
    );
  };

  // Render state pills
  const renderStatePill = (state, index, style = {}) => {
    if (!state) return null;

    const stateAbbr = state.state_abbreviation;
    const colorInfo = getStateColor(stateAbbr);

    return (
      <StateBadge
        key={`state-${state.state_id}-${index}`}
        style={{
          backgroundColor: colorInfo.color,
          color: colorInfo.textColor,
          borderColor: colorInfo.borderColor
        }}
        title={state.state_name}
      >
        {stateAbbr}
      </StateBadge>
    );
  };

  // Render pharmacy pills
  const renderPharmacyPill = (pharmacyId, index, style = {}) => {
    const pharmacy = pharmacies.find((p) => p.pharmacy_id === pharmacyId);
    if (!pharmacy) return null;

    return (
      <PharmacyBadge key={`pharmacy-${pharmacyId}-${index}`} style={style}>
        {pharmacy.pharmacy_name}
      </PharmacyBadge>
    );
  };

  // Render variant pills
  const renderVariantPill = (variant, index, style = {}) => {
    if (!variant) return null;

    return (
      <div
        key={`variant-${variant.variant_id}-${index}`}
        style={{
          padding: "0.5rem 0.75rem",
          borderRadius: "var(--border-radius)",
          backgroundColor: "var(--bg-secondary)",
          color: "var(--text-primary)",
          fontSize: "0.875rem"
        }}
      >
        {variant.quantity} {variant.unit} {variant.note && `- ${variant.note}`}
      </div>
    );
  };

  // Load medicines, states, and pharmacies data
  const loadData = async () => {
    setLoading(true);
    try {
      // Fetch medicines
      const medicinesData = await fetchMedications();
      setMedicines(medicinesData);
      setFilteredMedicines(medicinesData);

      // Fetch states
      const statesData = await fetchStates();
      setStates(statesData);

      // Fetch pharmacies
      const pharmaciesData = await fetchPharmacies();
      setPharmacies(pharmaciesData);

      setLoading(false);
    } catch (err) {
      console.error("Error loading data:", err);
      setError("Failed to load data. Please try again later.");
      setLoading(false);
    }
  };

  // Toggle expanded row for variants
  const toggleRowExpanded = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  useEffect(() => {
    loadData();

    // Get the current user from localStorage
    const userString = localStorage.getItem("user");
    if (userString) {
      try {
        const user = JSON.parse(userString);
        setCurrentUser(user);

        // Set permissions based on user role
        // Only superusers can edit/create/delete
        setHasEditPermission(user.is_superuser);
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, []);

  // Apply filters and sorting to medicines
  useEffect(() => {
    let result = [...medicines];

    // Apply search term filter
    if (searchTerm.trim() !== "") {
      result = result.filter(
        (medicine) =>
          medicine.medicine_name
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          medicine.medicine_sku
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          medicine.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply pharmacy filter
    if (filters.pharmacy) {
      result = result.filter(
        (medicine) =>
          medicine.pharmacies &&
          medicine.pharmacies.includes(parseInt(filters.pharmacy))
      );
    }

    // Apply state filter
    if (filters.state) {
      result = result.filter(
        (medicine) =>
          medicine.states &&
          medicine.states.some(
            (state) => state.state_id === parseInt(filters.state)
          )
      );
    }

    // Apply hasVariants filter
    if (filters.hasVariants) {
      if (filters.hasVariants === "yes") {
        result = result.filter(
          (medicine) => medicine.variants && medicine.variants.length > 0
        );
      } else if (filters.hasVariants === "no") {
        result = result.filter(
          (medicine) => !medicine.variants || medicine.variants.length === 0
        );
      }
    }

    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        // Handle nested properties
        if (sortConfig.key === "pharmacies_count") {
          const aValue = a.pharmacies ? a.pharmacies.length : 0;
          const bValue = b.pharmacies ? b.pharmacies.length : 0;

          if (sortConfig.direction === "asc") {
            return aValue - bValue;
          } else {
            return bValue - aValue;
          }
        } else if (sortConfig.key === "variants_count") {
          const aValue = a.variants ? a.variants.length : 0;
          const bValue = b.variants ? b.variants.length : 0;

          if (sortConfig.direction === "asc") {
            return aValue - bValue;
          } else {
            return bValue - aValue;
          }
        } else if (sortConfig.key === "states_count") {
          const aValue = a.states ? a.states.length : 0;
          const bValue = b.states ? b.states.length : 0;

          if (sortConfig.direction === "asc") {
            return aValue - bValue;
          } else {
            return bValue - aValue;
          }
        } else {
          // Standard string comparison for other fields
          if (a[sortConfig.key] < b[sortConfig.key]) {
            return sortConfig.direction === "asc" ? -1 : 1;
          }
          if (a[sortConfig.key] > b[sortConfig.key]) {
            return sortConfig.direction === "asc" ? 1 : -1;
          }
          return 0;
        }
      });
    }

    setFilteredMedicines(result);

    // Update active filters
    const newActiveFilters = {};
    if (filters.pharmacy) {
      const selectedPharmacy = pharmacies.find(
        (p) => p.pharmacy_id === parseInt(filters.pharmacy)
      );
      if (selectedPharmacy) {
        newActiveFilters.pharmacy = selectedPharmacy.pharmacy_name;
      }
    }

    if (filters.state) {
      const selectedState = states.find(
        (s) => s.state_id === parseInt(filters.state)
      );
      if (selectedState) {
        newActiveFilters.state = selectedState.state_name;
      }
    }

    if (filters.hasVariants) {
      newActiveFilters.hasVariants =
        filters.hasVariants === "yes" ? "Has variants" : "No variants";
    }

    setActiveFilters(newActiveFilters);
  }, [searchTerm, medicines, filters, sortConfig, pharmacies, states]);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle sort changes
  const handleSortChange = (e) => {
    const value = e.target.value;

    if (value === "") {
      setSortConfig({
        key: "medicine_name",
        direction: "asc"
      });
      return;
    }

    const [key, direction] = value.split("-");
    setSortConfig({
      key,
      direction
    });
  };

  // Remove single filter
  const removeFilter = (filterKey) => {
    setFilters((prev) => ({
      ...prev,
      [filterKey]: ""
    }));
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({
      pharmacy: "",
      state: "",
      hasVariants: ""
    });
    setSortConfig({
      key: "medicine_name",
      direction: "asc"
    });
  };

  // Handle opening the modal for editing an existing medicine
  const handleEditMedicine = (medicine) => {
    if (!hasEditPermission) return;

    setIsEditing(true);
    setEditingId(medicine.medicine_id);

    // Ensure dosage instructions have the correct format
    const formattedDosageInstructions = medicine.dosage_instructions
      ? medicine.dosage_instructions.map((instruction) => {
          // Support both object format and string format
          if (typeof instruction === "string") {
            return { dosage_instruction: instruction };
          } else if (typeof instruction === "object") {
            return {
              dosage_id: instruction.dosage_id,
              dosage_instruction: instruction.dosage_instruction
            };
          }
          return instruction;
        })
      : [];

    // Map the medicine data to the form structure
    setFormData({
      medicine_name: medicine.medicine_name || "",
      medicine_sku: medicine.medicine_sku || "",
      description: medicine.description || "",
      pharmacies: medicine.pharmacies || [],
      states: medicine.states
        ? medicine.states.map((state) => state.state_id)
        : [],
      variants: medicine.variants || [],
      dosage_instructions: formattedDosageInstructions
    });

    setShowModal(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirmation = (medicine) => {
    if (!hasEditPermission) return;

    setMedicineToDelete(medicine);
    setShowConfirmation(true);
  };

  // Handle actual deletion
  const handleDeleteMedicine = async () => {
    if (!medicineToDelete || !hasEditPermission) return;

    setLoading(true);
    try {
      await deleteMedicine(medicineToDelete.medicine_id);

      // Remove the deleted medicine from the state
      setMedicines((prev) =>
        prev.filter((m) => m.medicine_id !== medicineToDelete.medicine_id)
      );
      setFilteredMedicines((prev) =>
        prev.filter((m) => m.medicine_id !== medicineToDelete.medicine_id)
      );

      setShowConfirmation(false);
      setMedicineToDelete(null);
      setLoading(false);
    } catch (err) {
      console.error("Error deleting medicine:", err);
      setError("Failed to delete medicine. Please try again later.");
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!hasEditPermission) return;

    setLoading(true);
    try {
      console.log("formData of medication", formData);
      if (isEditing) {
        // Call the updated API and get the response
        const updatedMedicine = await updateMedicine(editingId, formData);
        console.log("updatedMedicine", updatedMedicine);
        // Update the medicines list with the response from the API
        // This ensures we have the correct structure with variant_ids, dosage_ids, etc.
        setMedicines((prev) =>
          prev.map((medicine) =>
            medicine.medicine_id === editingId ? updatedMedicine : medicine
          )
        );
        setFilteredMedicines((prev) =>
          prev.map((medicine) =>
            medicine.medicine_id === editingId ? updatedMedicine : medicine
          )
        );
      } else {
        const newMedicine = await createMedicine(formData);

        // Add the new medicine to the list
        setMedicines((prev) => [...prev, newMedicine]);
        setFilteredMedicines((prev) => [...prev, newMedicine]);
      }

      // Close the modal
      setShowModal(false);
      setLoading(false);
    } catch (err) {
      console.error("Error saving medicine:", err);
      setError("Failed to save medicine. Please try again later.");
      setLoading(false);
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  // Handle pharmacy selection
  const handlePharmacyChange = (pharmacyId) => {
    // Find the pharmacy object
    const pharmacy = pharmacies.find((p) => p.pharmacy_id === pharmacyId);

    // Toggle pharmacy selection
    let updatedPharmacies;
    if (formData.pharmacies.includes(pharmacyId)) {
      // If pharmacy is already selected, remove it
      updatedPharmacies = formData.pharmacies.filter((id) => id !== pharmacyId);

      // Also remove the states associated with this pharmacy if they're not associated with any other selected pharmacy
      const otherSelectedPharmacies = updatedPharmacies
        .map((id) => pharmacies.find((p) => p.pharmacy_id === id))
        .filter(Boolean);
      const otherPharmacyStates = otherSelectedPharmacies.flatMap(
        (p) => p.states || []
      );

      // Only keep states that are in other selected pharmacies
      const updatedStates = formData.states.filter((stateId) =>
        otherPharmacyStates.includes(stateId)
      );

      setFormData((prev) => ({
        ...prev,
        pharmacies: updatedPharmacies,
        states: updatedStates
      }));
    } else {
      // If pharmacy is not selected, add it
      updatedPharmacies = [...formData.pharmacies, pharmacyId];

      // Also add all states associated with this pharmacy
      const pharmacyStates = pharmacy?.states || [];

      // Combine current states with pharmacy states (without duplicates)
      const updatedStates = [
        ...new Set([...formData.states, ...pharmacyStates])
      ];

      setFormData((prev) => ({
        ...prev,
        pharmacies: updatedPharmacies,
        states: updatedStates
      }));
    }
  };

  // Handle state selection
  const handleStateChange = (stateId) => {
    setFormData((prev) => {
      if (prev.states.includes(stateId)) {
        return {
          ...prev,
          states: prev.states.filter((id) => id !== stateId)
        };
      } else {
        return {
          ...prev,
          states: [...prev.states, stateId]
        };
      }
    });
  };

  // Handle variant form input changes
  const handleVariantInputChange = (e) => {
    const { name, value } = e.target;
    setVariantForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  // Add a new variant
  const handleAddVariant = () => {
    if (!variantForm.quantity || !variantForm.unit) {
      alert("Quantity and unit are required for variants");
      return;
    }

    const newVariant = {
      variant_id: `temp-${Date.now()}`,
      ...variantForm
    };

    setFormData((prev) => ({
      ...prev,
      variants: [...prev.variants, newVariant]
    }));

    // Reset the variant form
    setVariantForm({
      quantity: "",
      unit: "tablet",
      note: ""
    });
  };

  // Remove a variant
  const handleRemoveVariant = (variantId) => {
    setFormData((prev) => ({
      ...prev,
      variants: prev.variants.filter(
        (variant) => variant.variant_id !== variantId
      )
    }));
  };

  // Add a new dosage instruction
  const handleAddDosageInstruction = () => {
    if (!dosageInstruction.trim()) return;

    const newInstruction = {
      dosage_instruction: dosageInstruction
    };

    setFormData((prev) => ({
      ...prev,
      dosage_instructions: [...prev.dosage_instructions, newInstruction]
    }));

    // Reset the dosage instruction input
    setDosageInstruction("");
  };





  // Handle removing a dosage instruction
  const handleRemoveDosageInstruction = (instructionIdOrIndex) => {
    setFormData((prev) => {
      return {
        ...prev,
        dosage_instructions: prev.dosage_instructions.filter(
          (instruction, index) => {
            // For existing instructions with dosage_id
            if (instruction.dosage_id !== undefined) {
              return instruction.dosage_id !== instructionIdOrIndex;
            }

            // For new instructions without dosage_id (using index)
            return index !== instructionIdOrIndex;
          }
        )
      };
    });
  };

  return (
    <div>
      <PageHeader title="Medicines" />

      {/* Display notice for view-only users */}
      {!hasEditPermission && (
        <AccessNotice>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22ZM12 20C16.418 20 20 16.418 20 12C20 7.582 16.418 4 12 4C7.582 4 4 7.582 4 12C4 16.418 7.582 20 12 20ZM11 15H13V17H11V15ZM11 7H13V13H11V7Z"
              fill="currentColor"
            />
          </svg>
          <div>
            <strong>View-only access:</strong> You can browse and search
            medicines, but you don't have permission to add, edit, or delete.
          </div>
        </AccessNotice>
      )}

      <Card>
        <SearchBar>
          <input
            type="text"
            placeholder="Search medicines..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {hasEditPermission && (
            <AddButton
              onClick={() => {
                setIsEditing(false);
                setFormData({
                  medicine_name: "",
                  medicine_sku: "",
                  description: "",
                  pharmacies: [],
                  states: [],
                  dosage_instructions: [],
                  variants: []
                });
                setShowModal(true);
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 6V12M12 12V18M12 12H18M12 12H6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Add Medicine
            </AddButton>
          )}
        </SearchBar>

        {/* Filter and Sort Controls */}
        <FilterSortContainer>
          <FilterGroup>
            <FilterLabel>Pharmacy</FilterLabel>
            <FilterSelect
              name="pharmacy"
              value={filters.pharmacy}
              onChange={handleFilterChange}
            >
              <option value="">All Pharmacies</option>
              {pharmacies.map((pharmacy) => (
                <option key={pharmacy.pharmacy_id} value={pharmacy.pharmacy_id}>
                  {pharmacy.pharmacy_name}
                </option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>State</FilterLabel>
            <FilterSelect
              name="state"
              value={filters.state}
              onChange={handleFilterChange}
            >
              <option value="">All States</option>
              {states.map((state) => (
                <option key={state.state_id} value={state.state_id}>
                  {state.state_name} ({state.state_abbreviation})
                </option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Has Variants</FilterLabel>
            <FilterSelect
              name="hasVariants"
              value={filters.hasVariants}
              onChange={handleFilterChange}
            >
              <option value="">All</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Sort By</FilterLabel>
            <FilterSelect
              name="sort"
              value={`${sortConfig.key}-${sortConfig.direction}`}
              onChange={handleSortChange}
            >
              <option value="medicine_name-asc">Name (A-Z)</option>
              <option value="medicine_name-desc">Name (Z-A)</option>
              <option value="medicine_sku-asc">SKU (A-Z)</option>
              <option value="medicine_sku-desc">SKU (Z-A)</option>
              <option value="pharmacies_count-desc">Most Pharmacies</option>
              <option value="pharmacies_count-asc">Least Pharmacies</option>
              <option value="variants_count-desc">Most Variants</option>
              <option value="variants_count-asc">Least Variants</option>
              <option value="states_count-desc">Most States</option>
              <option value="states_count-asc">Least States</option>
            </FilterSelect>
          </FilterGroup>
        </FilterSortContainer>

        {/* Display active filters */}
        {Object.keys(activeFilters).length > 0 && (
          <ActiveFiltersContainer>
            {Object.entries(activeFilters).map(([key, value]) => (
              <FilterChip key={key}>
                {key === "pharmacy"
                  ? "Pharmacy: "
                  : key === "state"
                  ? "State: "
                  : "Variants: "}
                {value}
                <span className="close-btn" onClick={() => removeFilter(key)}>
                  ×
                </span>
              </FilterChip>
            ))}
            <ClearFiltersButton onClick={clearAllFilters}>
              Clear all filters
            </ClearFiltersButton>
          </ActiveFiltersContainer>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            Loading medicines...
          </div>
        ) : error ? (
          <div
            style={{
              textAlign: "center",
              padding: "2rem",
              color: "var(--error-color)"
            }}
          >
            {error}
          </div>
        ) : (
          <div className="medicine-list">
            {filteredMedicines.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "2rem",
                  color: "var(--text-secondary)"
                }}
              >
                No medicines found.
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem"
                }}
              >
                {filteredMedicines.map((medicine) => (
                  <Card
                    key={medicine.medicine_id}
                    style={{
                      padding: "1.25rem",
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.75rem",
                      width: "100%"
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start"
                      }}
                    >
                      <div>
                        <h3
                          style={{
                            margin: "0 0 0.25rem 0",
                            fontSize: "1.125rem"
                          }}
                        >
                          {medicine.medicine_name}
                        </h3>
                        <div
                          style={{
                            fontSize: "0.75rem",
                            color: "var(--text-secondary)"
                          }}
                        >
                          {medicine.medicine_sku}
                        </div>
                      </div>
                      {hasEditPermission && (
                        <div style={{ display: "flex", gap: "0.5rem" }}>
                          <ActionButton
                            onClick={() => handleEditMedicine(medicine)}
                          >
                            Edit
                          </ActionButton>
                          {/* <DeleteActionButton
                            onClick={() => handleDeleteConfirmation(medicine)}
                          >
                            Delete
                          </DeleteActionButton> */}
                        </div>
                      )}
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.75rem"
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: "0.75rem",
                            fontWeight: "500",
                            marginBottom: "0.25rem",
                            color: "var(--text-secondary)"
                          }}
                        >
                          Pharmacy
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "0.5rem"
                          }}
                        >
                          {medicine.pharmacies &&
                          medicine.pharmacies.length > 0 ? (
                            medicine.pharmacies.map((pharmacyId, index) => {
                              const pharmacy = pharmacies.find(
                                (p) => p.pharmacy_id === pharmacyId
                              );
                              if (!pharmacy) return null;

                              return (
                                <div
                                  key={`pharmacy-${pharmacyId}-${index}`}
                                  style={{
                                    padding: "0.5rem 0.75rem",
                                    borderRadius: "var(--border-radius)",
                                    backgroundColor: "var(--bg-secondary)",
                                    color: "var(--text-primary)",
                                    fontSize: "0.875rem",
                                    width: "100%"
                                  }}
                                >
                                  {pharmacy.pharmacy_name}
                                </div>
                              );
                            })
                          ) : (
                            <span
                              style={{
                                color: "var(--text-secondary)",
                                fontSize: "0.875rem"
                              }}
                            >
                              No pharmacy assigned
                            </span>
                          )}
                        </div>
                      </div>

                      {medicine.variants && medicine.variants.length > 0 && (
                        <div>
                          <div
                            style={{
                              fontSize: "0.75rem",
                              fontWeight: "500",
                              marginBottom: "0.25rem",
                              color: "var(--text-secondary)"
                            }}
                          >
                            Variants
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "0.8rem",
                              margin: "0.2rem 0"
                            }}
                          >
                            {medicine.variants.map((variant, index) =>
                              renderVariantPill(variant, index, {
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "0.4rem 1.5rem",
                                borderRadius: "15px",
                                backgroundColor: "var(--bg-secondary)",
                                border:
                                  "1px solid rgba(var(--primary-color-rgb), 0.2)",
                                fontWeight: "500",
                                fontSize: "0.85rem",
                                boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                                minWidth: "150px",
                                minHeight: "36px",
                                textAlign: "center",
                                whiteSpace: "nowrap",
                                color: "var(--primary-dark)",
                                letterSpacing: "0.01em"
                              })
                            )}
                          </div>
                        </div>
                      )}

                      <div>
                        <div
                          style={{
                            fontSize: "0.75rem",
                            fontWeight: "500",
                            marginBottom: "0.25rem",
                            color: "var(--text-secondary)"
                          }}
                        >
                          Available States
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "0.25rem"
                          }}
                        >
                          {medicine.states && medicine.states.length > 0 ? (
                            medicine.states.map((state, index) =>
                              renderStatePill(state, index)
                            )
                          ) : (
                            <span
                              style={{
                                color: "var(--text-secondary)",
                                fontSize: "0.875rem"
                              }}
                            >
                              No states available
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Edit Medicine Modal */}
      {showModal && hasEditPermission && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>
                {isEditing ? "Edit Medicine" : "Add Medicine"}
              </ModalTitle>
              <CloseButton onClick={() => setShowModal(false)}>
                &times;
              </CloseButton>
            </ModalHeader>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: "1.5rem" }}>
                <Label htmlFor="medicine_name">Medicine Name</Label>
                <Input
                  type="text"
                  id="medicine_name"
                  name="medicine_name"
                  value={formData.medicine_name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div style={{ marginBottom: "1.5rem" }}>
                <Label htmlFor="medicine_sku">SKU</Label>
                <Input
                  type="text"
                  id="medicine_sku"
                  name="medicine_sku"
                  value={formData.medicine_sku}
                  onChange={handleInputChange}
                />
              </div>

              <div style={{ marginBottom: "1.5rem" }}>
                <Label htmlFor="description">Description</Label>
                <TextArea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                />
              </div>

              <div style={{ marginBottom: "1.5rem" }}>
                <Label>Pharmacies</Label>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "0.5rem",
                    marginTop: "0.5rem"
                  }}
                >
                  {pharmacies.map((pharmacy) => (
                    <div
                      key={pharmacy.pharmacy_id}
                      style={{
                        padding: "0.5rem 0.75rem",
                        borderRadius: "var(--border-radius)",
                        backgroundColor: formData.pharmacies.includes(
                          pharmacy.pharmacy_id
                        )
                          ? "var(--primary-color)"
                          : "var(--bg-secondary)",
                        color: formData.pharmacies.includes(
                          pharmacy.pharmacy_id
                        )
                          ? "white"
                          : "var(--text-primary)",
                        cursor: "pointer",
                        fontSize: "0.875rem"
                      }}
                      onClick={() => handlePharmacyChange(pharmacy.pharmacy_id)}
                    >
                      {pharmacy.pharmacy_name}
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: "1.5rem" }}>
                <Label>States</Label>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "0.5rem",
                    marginTop: "0.5rem"
                  }}
                >
                  {states.map((state) => (
                    <div
                      key={state.state_id}
                      style={{
                        padding: "0.5rem 0.75rem",
                        borderRadius: "var(--border-radius)",
                        backgroundColor: formData.states.includes(
                          state.state_id
                        )
                          ? "var(--primary-color)"
                          : "var(--bg-secondary)",
                        color: formData.states.includes(state.state_id)
                          ? "white"
                          : "var(--text-primary)",
                        cursor: "pointer",
                        fontSize: "0.875rem"
                      }}
                      onClick={() => handleStateChange(state.state_id)}
                    >
                      {state.state_abbreviation}
                    </div>
                  ))}
                </div>
              </div>

              {/* Dosage Instructions Section */}
              <div style={{ marginBottom: "1.5rem" }}>
                <Label>Dosage Instructions</Label>

                {formData.dosage_instructions &&
                formData.dosage_instructions.length > 0 ? (
                  <div style={{ marginBottom: "1rem" }}>
                    {formData.dosage_instructions.map((instruction, index) => (
                      <div
                        key={
                          instruction.dosage_id || `new-instruction-${index}`
                        }
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "0.75rem",
                          backgroundColor: "var(--bg-secondary)",
                          borderRadius: "var(--border-radius)",
                          marginBottom: "0.5rem"
                        }}
                      >
                        <div>{instruction.dosage_instruction}</div>
                        <button
                          type="button"
                          onClick={() =>
                            handleRemoveDosageInstruction(
                              instruction.dosage_id || index
                            )
                          }
                          style={{
                            background: "none",
                            border: "none",
                            color: "var(--error-color)",
                            cursor: "pointer",
                            fontSize: "1.25rem"
                          }}
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    style={{
                      marginBottom: "1rem",
                      color: "var(--text-secondary)"
                    }}
                  >
                    No dosage instructions added yet.
                  </div>
                )}

                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <div style={{ flex: "1" }}>
                    <Input
                      type="text"
                      placeholder="Enter dosage instruction"
                      value={dosageInstruction}
                      onChange={(e) => setDosageInstruction(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && dosageInstruction.trim()) {
                          e.preventDefault();
                          handleAddDosageInstruction();
                        }
                      }}
                    />
                  </div>
                  <AddButton
                    type="button"
                    onClick={handleAddDosageInstruction}
                    style={{ padding: "0 1rem" }}
                    disabled={!dosageInstruction.trim()}
                  >
                    Add
                  </AddButton>
                </div>
              </div>

              {/* Variants Section */}
              <div style={{ marginBottom: "1.5rem" }}>
                <Label>Variants</Label>

                {formData.variants && formData.variants.length > 0 ? (
                  <div style={{ marginBottom: "1rem" }}>
                    {formData.variants.map((variant, index) => (
                      <div
                        key={`variant-${variant.variant_id}-${index}`}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "0.5rem 0.75rem",
                          borderRadius: "var(--border-radius)",
                          backgroundColor: "var(--bg-secondary)",
                          color: "var(--text-primary)",
                          fontSize: "0.875rem",
                          marginBottom: "0.5rem"
                        }}
                      >
                        <div>
                          {variant.quantity} {variant.unit}{" "}
                          {variant.note && `- ${variant.note}`}
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            handleRemoveVariant(variant.variant_id)
                          }
                          style={{
                            background: "none",
                            border: "none",
                            color: "var(--error-color)",
                            cursor: "pointer",
                            fontSize: "1.25rem"
                          }}
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    style={{
                      marginBottom: "1rem",
                      color: "var(--text-secondary)"
                    }}
                  >
                    No variants added yet.
                  </div>
                )}

                <div
                  style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}
                >
                  <div style={{ flex: "1 1 200px" }}>
                    <Input
                      type="text"
                      placeholder="Quantity"
                      name="quantity"
                      value={variantForm.quantity}
                      onChange={handleVariantInputChange}
                    />
                  </div>
                  <div style={{ flex: "1 1 200px" }}>
                    <Select
                      name="unit"
                      value={variantForm.unit}
                      onChange={handleVariantInputChange}
                    >
                      <option value="tablet">Tablet</option>
                      <option value="capsule">Capsule</option>
                      <option value="ml">ml</option>
                      <option value="mg">mg</option>
                      <option value="g">g</option>
                      <option value="vial">vial</option>
                      <option value="each">each</option>
                      <option value="other">Other</option>
                    </Select>
                  </div>
                  <div style={{ flex: "1 1 200px" }}>
                    <Input
                      type="text"
                      placeholder="Note"
                      name="note"
                      value={variantForm.note}
                      onChange={handleVariantInputChange}
                    />
                  </div>
                  <div>
                    <AddButton
                      type="button"
                      onClick={handleAddVariant}
                      style={{ height: "100%", padding: "0 1rem" }}
                    >
                      Add
                    </AddButton>
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "2rem"
                }}
              >
                <CancelButton
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{ marginRight: "1rem" }}
                >
                  Cancel
                </CancelButton>
                <AddButton type="submit" disabled={loading}>
                  {loading
                    ? "Saving..."
                    : isEditing
                    ? "Update Medicine"
                    : "Add Medicine"}
                </AddButton>
              </div>
            </form>
          </ModalContent>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {showConfirmation && hasEditPermission && (
        <ConfirmationModal>
          <ConfirmationContent>
            <ModalHeader>
              <ModalTitle>Confirm Delete</ModalTitle>
              <CloseButton onClick={() => setShowConfirmation(false)}>
                &times;
              </CloseButton>
            </ModalHeader>

            <div style={{ marginBottom: "1rem" }}>
              <p>
                Are you sure you want to delete the medicine "
                {medicineToDelete?.medicine_name}"?
              </p>
              <p>This action cannot be undone.</p>
            </div>

            <ConfirmationActions>
              <CancelButton
                type="button"
                onClick={() => setShowConfirmation(false)}
              >
                Cancel
              </CancelButton>
              <DeleteButton
                type="button"
                onClick={handleDeleteMedicine}
                disabled={loading}
              >
                {loading ? "Deleting..." : "Delete"}
              </DeleteButton>
            </ConfirmationActions>
          </ConfirmationContent>
        </ConfirmationModal>
      )}
    </div>
  );
}

export default MedicinesPage;
